import { useState } from "react";
import moment from "moment";

/**
 * Custom hook for downloading all data from a table
 * @param {Function} lazyQuery - RTK Query lazy query hook
 * @param {string} fileName - Name of the downloaded file (without extension)
 * @returns {Object} - { handleDownloadAll, isDownloading }
 */
export default function useDownloadAll(lazyQuery, fileName = "Data") {
  const [trigger, { isFetching }] = lazyQuery();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadAll = async (queryParams = {}) => {
    setIsDownloading(true);
    try {
      const params = {
        ...queryParams,
        limit: 100000,
        skip: 0,
      };

      const result = await trigger(params).unwrap();
      const allData = result?.data || [];
      const columns = result?.columns || [];

      if (allData.length === 0) {
        setIsDownloading(false);
        return;
      }

      // Generate CSV content
      const headers = columns
        .filter((col) => col.isDownloadable)
        .map((col) => col.headerName);

      const csvRows = allData.map((row) =>
        columns
          .filter((col) => col.isDownloadable)
          .map((col) => {
            const value = row[col.columnName];
            if (col.type === "date" && value) {
              return moment(value).format("DD/MM/YYYY");
            }
            if (col.type === "boolean") {
              return value ? "Yes" : "No";
            }
            // Escape commas and quotes
            if (typeof value === "string") {
              const escaped = value.replace(/"/g, '""');
              return value.includes(",") || value.includes('"')
                ? `"${escaped}"`
                : value;
            }
            return value ?? "";
          })
          .join(","),
      );

      csvRows.unshift(headers.join(","));
      const csvContent = csvRows.join("\n");

      // Download the file
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      // Handle error silently
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    handleDownloadAll,
    isDownloading: isDownloading || isFetching,
  };
}




